import { useRef, useState } from 'react';
import { CandyCane, FileDown, Gift, Loader2, Snowflake, Star, TreePine } from 'lucide-react';
import type { getStarsReport } from '@generated/server/stars';
import { exportCardsToPdf } from '@generated/components/pagedPdf';
import { RHSR_LOGO } from '@generated/components/AdGuide';

type Report = Awaited<ReturnType<typeof getStarsReport>>;
type Cat = Report['categories'][number];

// 5 × 7 in card drawn at 96 px per inch; the PDF page is 360 × 504 pt (exactly 5 × 7 in).
const W = 480, H = 672;
const short = (label: string) => (/community organization/i.test(label) ? 'Local Non-Profit' : label.replace(/^Best\s+/i, ''));

function Box({ c }: { c: Cat }) {
  return (
    <div className="break-inside-avoid border-t-[1.5px] border-foreground pt-[3px]">
      <div className="text-[10.5px] font-black uppercase leading-[12.5px] tracking-[0.02em]">{short(c.label)}</div>
      <ul className="mt-[3px] space-y-[2.5px]">
        {c.top.length === 0 ? <li className="text-[10px] italic leading-[12px] text-muted-foreground">No nominees</li> : c.top.map((t) => (
          <li key={t.name} className="flex items-start gap-[5px] text-[10px] leading-[12.5px]">
            <span className="mt-[1px] h-[10px] w-[10px] shrink-0 border-[1.4px] border-foreground" />
            <span className="min-w-0 break-words">{t.name}</span>
          </li>))}
      </ul>
    </div>
  );
}

function Grid({ cats, rows = 8 }: { cats: Cat[]; rows?: number }) {
  return <div className={`grid h-full grid-flow-col grid-cols-2 gap-x-[16px] gap-y-[9px] ${rows === 7 ? "grid-rows-7" : "grid-rows-8"}`}>{cats.map((c) => <Box key={c.label} c={c} />)}</div>;
}

function Front({ cats }: { cats: Cat[] }) {
  return (
    <div className="flex h-full w-full flex-col bg-white px-[22px] py-[16px] font-sans text-foreground">
      <div className="flex items-center gap-3 border-b-[3px] border-double border-foreground pb-1.5">
        <img src={RHSR_LOGO} alt="" crossOrigin="anonymous" className="h-12 w-12 shrink-0 object-contain" />
        <div className="min-w-0">
          <div className="text-[30px] font-black uppercase leading-none tracking-tight text-primary">★ Red Hook Stars ★</div>
          <div className="mt-1 text-[9.5px] font-bold uppercase tracking-[0.15em]">Community Appreciation Awards · Final Ballot</div>
        </div>
      </div>
      <p className="mt-1.5 text-[8.5px] font-semibold leading-[11.5px]">Our readers nominated the top three in every category. <strong>Check ONE favorite in each</strong>, then drop your ballot in any Star-Revue ballot box. A list of ballot boxes is on our portal site: <strong>bit.ly/star-revue</strong>. Or take a photo of each side with your phone and text it to us at <strong>917-652-9128</strong>.</p>
      <div className="mt-2.5 min-h-0 flex-1"><Grid cats={cats} /></div>
      <div className="mt-1 text-center text-[8px] font-bold uppercase tracking-[0.12em]">More categories on the back →</div>
    </div>
  );
}

function Back({ cats }: { cats: Cat[] }) {
  return (
    <div className="flex h-full w-full flex-col bg-white px-[22px] py-[16px] font-sans text-foreground">
      <div className="flex items-center justify-between border-b-[3px] border-double border-foreground pb-1">
        <div className="text-[13px] font-black uppercase tracking-tight text-primary">★ Red Hook Stars 2026 · Final Ballot · Page Two</div>
        <img src={RHSR_LOGO} alt="" crossOrigin="anonymous" className="h-7 w-7 object-contain" />
      </div>
      <div className="mt-2.5 min-h-0 flex-1"><Grid cats={cats} rows={7} /></div>
      <div className="mt-3 bg-[hsl(var(--brand-blue))] px-4 py-4 text-center text-[14.5px] font-bold leading-[20px] text-primary-foreground">
        Voting closes November 27. Winners announced in our special Holiday Edition of the <span className="font-black text-[hsl(var(--brand-red-bright))]">Red Hook Star-Revue</span>, out the first week of December.
        <div className="mt-2 flex items-center justify-center gap-3 text-[hsl(var(--brand-yellow))]" aria-hidden>
          <CandyCane strokeWidth={2.5} className="h-5 w-5 text-primary-foreground" /><Snowflake className="h-4 w-4 text-primary-foreground" /><TreePine className="h-5 w-5 fill-[hsl(var(--brand-green))] text-[hsl(var(--brand-green))]" /><Gift className="h-4 w-4 text-primary-foreground" /><Star className="h-5 w-5 fill-current" /><Gift className="h-4 w-4 text-primary-foreground" /><TreePine className="h-5 w-5 fill-[hsl(var(--brand-green))] text-[hsl(var(--brand-green))]" /><Snowflake className="h-4 w-4 text-primary-foreground" /><CandyCane strokeWidth={2.5} className="h-5 w-5 -scale-x-100 text-primary-foreground" />
        </div>
      </div>
    </div>
  );
}

/** Printable two-sided 5 × 7 final-round ballot built from the live top three in each category. */
export function StarsBallot({ r, page = false }: { r: Report; page?: boolean }) {
  const front = useRef<HTMLDivElement>(null);
  const back = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cats = r.categories;
  const frontCats = cats.slice(0, 16), backCats = cats.slice(16);
  const save = async () => {
    if (!front.current || !back.current) return;
    setBusy(true); setError(null);
    try { await exportCardsToPdf([front.current, back.current], 'red-hook-stars-final-ballot-5x7.pdf', 360, 504); }
    catch (e) { console.error(e); setError('The ballot PDF could not be made. Please try again.'); }
    finally { setBusy(false); }
  };
  const card = 'shrink-0 overflow-hidden border border-border shadow-[4px_4px_0_hsl(var(--foreground)/0.15)]';
  return (
    <section className={page ? "print:hidden" : "mt-12 border-t-4 border-double border-foreground pt-8 print:hidden"}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-[family-name:var(--font-heading)] text-3xl font-semibold">Final-round ballot · 5 × 7 card</h2>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">Phase two starts November 11. Front and back of the printed ballot, filled in from the current top three in each category. Make the final PDF after the first round closes November 10 so it shows the real finalists.</p>
        </div>
        <button type="button" disabled={busy} onClick={() => void save()} className="inline-flex min-h-11 items-center gap-2 border border-foreground px-4 text-sm font-semibold disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}Save ballot PDF (5 × 7, 2 sides)</button>
      </div>
      {error && <p role="alert" className="mt-3 text-sm text-destructive">{error}</p>}
      <div className="mt-6 flex max-w-full gap-6 overflow-x-auto bg-muted p-4">
        <div><div className="mb-1 text-xs font-semibold uppercase tracking-wider">Front</div><div ref={front} className={`${card} h-[672px] w-[480px]`}><Front cats={frontCats} /></div></div>
        <div><div className="mb-1 text-xs font-semibold uppercase tracking-wider">Back</div><div ref={back} className={`${card} h-[672px] w-[480px]`}><Back cats={backCats} /></div></div>
      </div>
    </section>
  );
}

export const BALLOT_SIZE = { W, H };
